import { useState } from "react";
import { motion } from "framer-motion";
import { ArrowUpRight, Check } from "lucide-react";
import { Reveal, Eyebrow, scrollToId } from "./Reveal";
import { useLang } from "../../i18n";

export const PLANS = [
  { id: "XS", area: "1", price: 35, fill: 0.11 },
  { id: "S", area: "2", price: 70, fill: 0.22 },
  { id: "M", area: "4", price: 130, fill: 0.44, popular: true },
  { id: "L", area: "6", price: 180, fill: 0.67 },
  { id: "XL", area: "9", price: 250, fill: 1 },
];

const Visual = ({ plan, t }) => (
  <div data-testid="size-visualizer" className="relative mx-auto aspect-square w-full max-w-[260px] sm:max-w-[340px]">
    <div className="absolute inset-0 rounded-3xl border-2 border-dashed border-[#A69B8F]/50" />
    <span className="absolute -top-6 left-0 text-[11px] font-bold uppercase tracking-widest text-[#7a7065]">{t.scale}</span>
    <motion.div
      className="absolute bottom-0 left-0 rounded-3xl bg-[#F26D5A]"
      animate={{ width: `${Math.sqrt(plan.fill) * 100}%`, height: `${Math.sqrt(plan.fill) * 100}%` }}
      transition={{ type: "spring", stiffness: 110, damping: 18 }}
    >
      <div className="absolute inset-x-3 top-3 h-[3px] rounded bg-[#1f1f1f]/25" />
      <div className="absolute inset-x-3 top-5 h-[3px] rounded bg-[#1f1f1f]/20" />
      <div className="absolute inset-x-3 top-7 h-[3px] rounded bg-[#1f1f1f]/15" />
    </motion.div>
    <div className="absolute right-4 top-4 text-right">
      <p className="font-display text-6xl font-extrabold leading-none text-[#2D2D2D]">{plan.id}</p>
      <p className="mt-1 text-sm font-bold text-[#5a524a]">{plan.area} m²</p>
    </div>
  </div>
);

const PlanCard = ({ plan, active, onHover, onReserve, t }) => (
  <button
    type="button"
    onMouseEnter={onHover}
    onFocus={onHover}
    onClick={onHover}
    data-testid={`pricing-card-${plan.id}`}
    className={`group relative flex h-full w-full flex-col rounded-3xl p-6 text-left transition-[background-color,color,transform,box-shadow] duration-500 sm:p-7 lg:p-5 xl:p-6 ${
      active ? "bg-[#2D2D2D] text-[#FDFBF7] shadow-[0_40px_80px_-40px_rgba(45,45,45,0.7)] lg:-translate-y-2" : "border border-[#A69B8F]/35 bg-white text-[#2D2D2D]"
    }`}
  >
    {plan.popular && (
      <span className="absolute right-5 top-5 rounded-full bg-[#F7D048] px-3 py-1 text-[10px] font-extrabold uppercase tracking-widest text-[#2D2D2D]">
        {t.popular}
      </span>
    )}
    <p className="font-display text-5xl font-extrabold leading-none lg:text-[2.6rem]">{plan.id}</p>
    <p className={`mt-2 text-sm font-bold ${active ? "text-[#F7D048]" : "text-[#C9503F]"}`}>{plan.area} m²</p>
    <p className={`mt-5 min-h-[3rem] text-sm leading-relaxed ${active ? "text-[#D9D2C8]" : "text-[#5a524a]"}`}>{t.plans[plan.id]}</p>
    <p className="mt-auto flex flex-wrap items-baseline gap-x-1.5 pt-6">
      <span className={`text-xs font-semibold ${active ? "text-[#D9D2C8]" : "text-[#7a7065]"}`}>{t.from}</span>
      <span className="font-display text-4xl font-extrabold lg:text-[2rem]" data-testid={`pricing-price-${plan.id}`}>{plan.price} €</span>
      <span className={`text-sm ${active ? "text-[#D9D2C8]" : "text-[#7a7065]"}`}>{t.month}</span>
    </p>
    <span
      role="button"
      tabIndex={0}
      onClick={(e) => { e.stopPropagation(); onReserve(); }}
      onKeyDown={(e) => { if (e.key === "Enter") { e.stopPropagation(); onReserve(); } }}
      data-testid={`pricing-reserve-btn-${plan.id}`}
      className={`mt-7 inline-flex items-center justify-between rounded-full px-5 py-3.5 text-sm font-bold transition-[background-color,color] duration-300 ${
        active ? "bg-[#F26D5A] text-[#1f1f1f] hover:bg-[#F7D048]" : "bg-[#F4F0E8] text-[#2D2D2D] hover:bg-[#F26D5A]"
      }`}
    >
      {t.reserve} <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:rotate-45" />
    </span>
  </button>
);

export const Pricing = ({ onSelect }) => {
  const [active, setActive] = useState("M");
  const { t: all } = useLang();
  const t = all.pricing;
  const plan = PLANS.find((p) => p.id === active);
  const reserve = (id) => { onSelect(id); scrollToId("reserva"); };

  return (
    <section id="mides" data-testid="pricing-section" className="bg-[#F4F0E8] py-24 sm:py-32">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <div className="grid gap-14 lg:grid-cols-[0.8fr_1.2fr] lg:items-center">
          <Reveal>
            <Eyebrow>{t.eyebrow}</Eyebrow>
            <h2 className="font-display mt-5 text-4xl font-extrabold leading-[1] tracking-tight text-[#2D2D2D] sm:text-5xl">
              {t.title[0]} <span className="italic text-[#F26D5A]">{t.title[1]}</span>
            </h2>
            <p className="mt-5 max-w-md text-base text-[#5a524a] md:text-lg">
              {t.intro}
            </p>
            <ul className="mt-6 space-y-2 text-sm font-semibold text-[#2D2D2D]">
              {t.bullets.map((b) => (
                <li key={b} className="flex items-center gap-2"><Check className="h-4 w-4 text-[#C9503F]" />{b}</li>
              ))}
            </ul>
          </Reveal>
          <Reveal delay={0.1}><Visual plan={plan} t={t} /></Reveal>
        </div>
        <div className="mt-16 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-5" data-testid="pricing-grid">
          {PLANS.map((p, i) => (
            <Reveal key={p.id} delay={i * 0.08} className="h-full">
              <PlanCard plan={p} active={active === p.id} onHover={() => setActive(p.id)} onReserve={() => reserve(p.id)} t={t} />
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};
